import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
  LocalFireDepartment as FireIcon,
  AutoFixHigh as MagicIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import { RootState } from '../../store';
import WebsiteSelector from '../../components/common/WebsiteSelector';
import { ContentOptimizer } from '../../components/content-optimizer/ContentOptimizer';
import { MetaDescriptionGenerator } from '../../components/meta-description';
import { AIRoast } from '../../components/ai-roast';
import { supabase } from '../../services/supabase';

interface ContentProject {
  id: string;
  project_name: string;
  primary_keyword: string;
  status: 'research' | 'writing' | 'published';
  created_at: string;
  updated_at: string;
  notes: string;
  target_url: string;
  published_url_id?: string;
  archived: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`content-studio-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const ContentStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Projects state
  const [projects, setProjects] = useState<ContentProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProject, setNewProject] = useState({
    project_name: '',
    primary_keyword: '',
    notes: '',
    target_url: ''
  });
  const [projectTabValue, setProjectTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<ContentProject | null>(null);

  // Load projects when website changes
  useEffect(() => {
    if (selectedWebsite?.website_id && tabValue === 0) {
      fetchProjects();
    }
  }, [selectedWebsite, tabValue, projectTabValue]);

  const fetchProjects = async () => {
    if (!selectedWebsite?.website_id) return;

    setProjectsLoading(true);
    try {
      let query = (supabase as any)
        .from('content_projects')
        .select('*')
        .eq('website_id', selectedWebsite.website_id)
        .order('created_at', { ascending: false });

      if (projectTabValue === 0) {
        query = query.eq('archived', false);
      } else if (projectTabValue === 2) {
        query = query.eq('archived', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const createProject = async () => {
    if (!selectedWebsite?.website_id || !newProject.project_name) return;

    try {
      const { error } = await (supabase as any)
        .from('content_projects')
        .insert([{
          website_id: selectedWebsite.website_id,
          ...newProject
        }]);

      if (error) throw error;

      setShowCreateModal(false);
      setNewProject({ project_name: '', primary_keyword: '', notes: '', target_url: '' });
      fetchProjects();
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const archiveProject = async (project: ContentProject) => {
    try {
      const { error } = await (supabase as any)
        .from('content_projects')
        .update({ archived: !project.archived, updated_at: new Date().toISOString() })
        .eq('id', project.id);

      if (error) throw error;
      fetchProjects();
      handleCloseMenu();
    } catch (error) {
      console.error('Error archiving project:', error);
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, project: ContentProject) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'research': return 'warning';
      case 'writing': return 'info';
      case 'published': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon sx={{ fontSize: 36, color: '#cc081c' }} />
              Content Studio
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create, optimize, and review your content - all in one place
            </Typography>
          </Box>
          <WebsiteSelector showAddButton={false} />
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': { py: 2 },
            '& .Mui-selected': { color: '#cc081c' },
            '& .MuiTabs-indicator': { bgcolor: '#cc081c' }
          }}
        >
          <Tab icon={<FolderIcon />} label="Projects" iconPosition="start" />
          <Tab icon={<MagicIcon />} label="Optimizer" iconPosition="start" />
          <Tab icon={<DescriptionIcon />} label="Meta Descriptions" iconPosition="start" />
          <Tab icon={<FireIcon />} label="AI Roast" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Projects */}
      <TabPanel value={tabValue} index={0}>
        {!selectedWebsite ? (
          <Alert severity="info">Please select a website to view content projects</Alert>
        ) : (
          <Box>
            {/* Projects Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Paper sx={{ flex: 1, mr: 2 }}>
                <Tabs
                  value={projectTabValue}
                  onChange={(e, v) => setProjectTabValue(v)}
                  sx={{ '& .MuiTabs-indicator': { display: 'none' } }}
                >
                  <Tab label="Active" />
                  <Tab label="All" />
                  <Tab label="Archived" />
                </Tabs>
              </Paper>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{ bgcolor: '#cc081c', '&:hover': { bgcolor: '#6250e6' } }}
              >
                New Project
              </Button>
            </Box>

            {/* Projects Grid */}
            {projectsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
            ) : projects.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <FolderIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No projects found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Create your first content project to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowCreateModal(true)}
                  sx={{ bgcolor: '#cc081c', '&:hover': { bgcolor: '#6250e6' } }}
                >
                  Create Project
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {projects.map((project) => (
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} key={project.id}>
                    <Card
                      onClick={() => navigate(`/content-projects/${project.id}`)}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, cursor: 'pointer' }
                      }}
                    >
                      <Box sx={{ p: 3, flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {project.project_name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenMenu(e, project);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>

                        {project.primary_keyword && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Keyword:</strong> {project.primary_keyword}
                          </Typography>
                        )}

                        {project.target_url && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Target:</strong> {project.target_url}
                          </Typography>
                        )}

                        {project.notes && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {project.notes.length > 80 ? project.notes.substring(0, 80) + '...' : project.notes}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                          label={project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                          color={getStatusColor(project.status) as any}
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(project.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </TabPanel>

      {/* Tab 1: Content Optimizer */}
      <TabPanel value={tabValue} index={1}>
        <ContentOptimizer />
      </TabPanel>

      {/* Tab 2: Meta Descriptions */}
      <TabPanel value={tabValue} index={2}>
        <MetaDescriptionGenerator />
      </TabPanel>

      {/* Tab 3: AI Roast */}
      <TabPanel value={tabValue} index={3}>
        <AIRoast />
      </TabPanel>

      {/* Create Project Dialog */}
      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Content Project</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={newProject.project_name}
              onChange={(e) => setNewProject({...newProject, project_name: e.target.value})}
              placeholder="Ultimate Guide to SEO..."
              sx={{ mb: 3 }}
              required
            />
            <TextField
              fullWidth
              label="Primary Keyword"
              value={newProject.primary_keyword}
              onChange={(e) => setNewProject({...newProject, primary_keyword: e.target.value})}
              placeholder="seo best practices"
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Target URL (planned)"
              value={newProject.target_url}
              onChange={(e) => setNewProject({...newProject, target_url: e.target.value})}
              placeholder="/blog/ultimate-seo-guide"
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Notes"
              value={newProject.notes}
              onChange={(e) => setNewProject({...newProject, notes: e.target.value})}
              placeholder="Research notes, content ideas..."
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button
            onClick={createProject}
            variant="contained"
            disabled={!newProject.project_name}
            sx={{ bgcolor: '#cc081c', '&:hover': { bgcolor: '#6250e6' } }}
          >
            Create Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          if (selectedProject) {
            navigate(`/content-projects/${selectedProject.id}`);
          }
          handleCloseMenu();
        }}>
          <LaunchIcon sx={{ mr: 1, fontSize: 20 }} /> Open Research Hub
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedProject) {
            archiveProject(selectedProject);
          }
        }}>
          {selectedProject?.archived ? (
            <>
              <UnarchiveIcon sx={{ mr: 1, fontSize: 20 }} /> Unarchive
            </>
          ) : (
            <>
              <ArchiveIcon sx={{ mr: 1, fontSize: 20 }} /> Archive
            </>
          )}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ContentStudioPage;
